import { __ } from '@/hub-shared/utils/i18n';
import { extractWpRestErrorCode, fetchWithNonce } from './fetchWithNonce';
import { parseApiError } from './parseApiError';

export type ComptExpertOffice = {
  id: string;
  name: string;
};

export type ComptExpertOfficeChoice = {
  idRequest: string;
  offices: ComptExpertOffice[];
};

export type ComptExpertConnectionHandlers = {
  apiBaseUrl: string;
  restBasePath: string;
  nonce: string;
  onSessionExpired: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  onChooseOffice?: (choice: ComptExpertOfficeChoice) => void;
};

/**
 * The 409s worth retrying with the same id_request: ComptExpert tokens not ready yet, or another
 * connection holding the cabinet's bind lock (the server keeps the request for that retry). The
 * other 409s are final answers.
 */
const RETRYABLE_CONFLICT_CODES = ['jse_auth_fetch_failed', 'jse_bind_busy'];
const MAX_TOKEN_FETCH_RETRIES = 5;

type AuthFetchOutcome =
  | { kind: 'connected' }
  | { kind: 'choice'; offices: ComptExpertOffice[] }
  | { kind: 'invalid' };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseOffices = (value: unknown): ComptExpertOffice[] | null => {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  const offices: ComptExpertOffice[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || entry.id === '' || typeof entry.name !== 'string') {
      return null;
    }
    if (seen.has(entry.id)) {
      continue;
    }
    seen.add(entry.id);
    offices.push({ id: entry.id, name: entry.name });
  }
  return offices;
};

const readAuthFetchOutcome = async (response: Response): Promise<AuthFetchOutcome> => {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { kind: 'invalid' };
  }
  const data = isRecord(payload) ? payload.data : undefined;
  if (!isRecord(data) || data.needs_office_choice !== true) {
    return { kind: 'connected' };
  }
  const offices = parseOffices(data.offices);
  return offices ? { kind: 'choice', offices } : { kind: 'invalid' };
};

const isRetryableConflict = async (response: Response): Promise<boolean> => {
  try {
    const code = extractWpRestErrorCode(await response.clone().json());
    return code === null || RETRYABLE_CONFLICT_CODES.includes(code);
  } catch {
    return true;
  }
};

const postAuthFetch = (
  handlers: ComptExpertConnectionHandlers,
  body: { id_request: string; office_id?: string },
): Promise<Response> =>
  fetchWithNonce(`${handlers.apiBaseUrl}${handlers.restBasePath}/auth/fetch`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': handlers.nonce,
    },
    body: JSON.stringify(body),
  });

const fetchComptExpertTokens = async (
  handlers: ComptExpertConnectionHandlers,
  idRequest: string,
  attempt = 0,
): Promise<void> => {
  const response = await postAuthFetch(handlers, { id_request: idRequest });

  if (response.status === 401) {
    handlers.onSessionExpired();
    return;
  }

  if (response.status === 409 && attempt < MAX_TOKEN_FETCH_RETRIES && (await isRetryableConflict(response))) {
    await new Promise((resolve) => {
      window.setTimeout(resolve, 1000);
    });
    await fetchComptExpertTokens(handlers, idRequest, attempt + 1);
    return;
  }

  if (!response.ok) {
    handlers.onError(await parseApiError(response));
    return;
  }

  const outcome = await readAuthFetchOutcome(response);
  if (outcome.kind === 'invalid' || (outcome.kind === 'choice' && !handlers.onChooseOffice)) {
    handlers.onError(__('Réponse serveur invalide.'));
    return;
  }
  if (outcome.kind === 'choice') {
    handlers.onChooseOffice?.({ idRequest, offices: outcome.offices });
    return;
  }

  handlers.onSuccess();
};

export const chooseComptExpertOffice = async (
  handlers: ComptExpertConnectionHandlers,
  idRequest: string,
  officeId: string,
): Promise<void> => {
  try {
    const response = await postAuthFetch(handlers, { id_request: idRequest, office_id: officeId });

    if (response.status === 401) {
      handlers.onSessionExpired();
      return;
    }

    if (!response.ok) {
      handlers.onError(await parseApiError(response));
      return;
    }

    handlers.onSuccess();
  } catch {
    handlers.onError(__('Impossible de relier ce cabinet JeSignExpert.'));
  }
};

export const startComptExpertAuth = async (
  handlers: ComptExpertConnectionHandlers,
  options?: {
    popup?: Window | null;
    onMessageListenerAttached?: (listener: (event: MessageEvent) => void) => void;
  },
): Promise<void> => {
  const popup = options?.popup ?? window.open('about:blank', `${handlers.restBasePath}_auth`, 'width=720,height=800');
  if (!popup) {
    handlers.onError(__("La fenêtre d'authentification a été bloquée."));
    return;
  }

  try {
    const response = await fetchWithNonce(`${handlers.apiBaseUrl}${handlers.restBasePath}/auth/start`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': handlers.nonce,
      },
      body: JSON.stringify({}),
    });

    if (response.status === 401) {
      popup.close();
      handlers.onSessionExpired();
      return;
    }

    if (!response.ok) {
      popup.close();
      handlers.onError(await parseApiError(response));
      return;
    }

    const payload = (await response.json()) as { data?: { auth_url?: string; id_request?: string } };
    const authUrl = payload.data?.auth_url;
    const idRequest = payload.data?.id_request;
    if (typeof authUrl !== 'string' || authUrl === '' || typeof idRequest !== 'string' || idRequest === '') {
      popup.close();
      handlers.onError(__('Réponse serveur invalide.'));
      return;
    }

    const listener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data ?? {};
      if (data.source !== 'accounting-hub-jesignexpert') {
        return;
      }

      window.removeEventListener('message', listener);
      void fetchComptExpertTokens(handlers, idRequest);
    };

    options?.onMessageListenerAttached?.(listener);
    window.addEventListener('message', listener);
    popup.location.href = authUrl;
  } catch {
    popup.close();
    handlers.onError(__('Impossible de démarrer la connexion JeSignExpert.'));
  }
};

export const disconnectComptExpert = async (handlers: ComptExpertConnectionHandlers): Promise<void> => {
  try {
    const response = await fetchWithNonce(`${handlers.apiBaseUrl}${handlers.restBasePath}/disconnect`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': handlers.nonce,
      },
      body: JSON.stringify({}),
    });

    if (response.status === 401) {
      handlers.onSessionExpired();
      return;
    }

    if (!response.ok) {
      handlers.onError(await parseApiError(response));
      return;
    }

    handlers.onSuccess();
  } catch {
    handlers.onError(__('Impossible de déconnecter JeSignExpert.'));
  }
};
